import { startRegistration, startWaitlistRegistration } from '../redux/registrationForm/regCart';
import {
  getCheckoutErrors,
  getRegisterErrors,
  getUpdateErrors,
  isExcludedByPrivacySettings
} from '../redux/registrationForm/errors';
import {
  openAlreadyRegisteredDialog, openEventStatusDialog,
  openEventTemporaryClosedErrorDialog,
  openEventWaitlistDialog, openNoAdmissionItemAvailableForRegistrationTypeDialog,
  openPrivateEventErrorDialog
} from '../dialogs';
import { redirectToExternalAuth, redirectToPage, routeToPage } from '../redux/pathInfo';
import { getDefaultWebsitePageId } from '../redux/website';
import { updateAdmin } from '../redux/registrationForm/regCart/actions';
import { isWaitlistEnabled } from '../redux/selectors/event';
import { getAssociatedRegistrationPathId } from '../redux/selectors/shared';
import { loadGuestRegistrationContent, loadRegistrationContent } from '../redux/actions';
import { REGISTRATION, WAITLIST } from '../redux/website/registrationProcesses';
import { getRegistrationPathIdOrNull } from '../redux/selectors/currentRegistrationPath';
import * as eventStatus from 'event-widgets/clients/EventStatus';
import { logoutPlanner } from '../redux/registrantLogin/actions';
import { getRegistrationTypeId, getAdminPersonalInformation } from '../redux/selectors/currentRegistrant';
import * as registrationTypeUtils from 'event-widgets/utils/registrationType';
import { hasRegTypeCapacityWarning } from '../redux/registrationForm/warnings';
import { regTypeCapacityFull } from '../dialogs/selectionConflictDialogs';
import { getPageWithRegistrationType } from '../redux/website/pageContents';

export function beginNewRegistration(changePageOverride = null) {
  // eslint-disable-next-line complexity
  return async (dispatch, getState) => {
    // Use history.replace or normal route change depending on scenario.
    const changePage = changePageOverride || (pageId => dispatch(routeToPage(pageId)));

    const initialState = getState();
    const { eventId, inviteeId,
      contactId, defaultRegTypeId } = initialState.userSession;
    try {
      await dispatch(startRegistration(eventId, inviteeId, contactId, defaultRegTypeId));
    } catch (error) {
      if (isExcludedByPrivacySettings(error, getState())
        || getUpdateErrors.isAttendeeNotAllowedByCustomLogic(error, getState())) {
        await dispatch(openPrivateEventErrorDialog());
        changePage(getDefaultWebsitePageId(getState()));
        return;
      }
      if (getUpdateErrors.handleAuthError(error, getState().account, getState().event)) {
        return;
      }
      if (getRegisterErrors.isInviteeRegistered(error)) {
        changePage(getDefaultWebsitePageId(getState()));
        await dispatch(
          openAlreadyRegisteredDialog({ title: 'EventGuestSide_ExistingInvitee_AlreadyRegistered_Header__resx' }));
        return;
      }
      if (getRegisterErrors.isInviteeAlreadyWaitlistedForClosedEvent(error)) {
        changePage(getDefaultWebsitePageId(getState()));
        await dispatch(openEventWaitlistDialog());
        return;
      }

      const waitlistEnabled = isWaitlistEnabled(getState(),
        getAssociatedRegistrationPathId(getState(), getState().userSession.defaultRegTypeId));
      if (getRegisterErrors.isEventTemporaryClosed(error)) {
        await dispatch(openEventTemporaryClosedErrorDialog());
        return;
      }
      if ((getRegisterErrors.isEventClosed(error) || getRegisterErrors.isCapacityFull(error)) && waitlistEnabled) {
        try {
          await dispatch(startWaitlistRegistration(inviteeId));
        } catch (waitlistError) {
          if (getRegisterErrors.isInviteeRegistered(waitlistError)) {
            changePage(getDefaultWebsitePageId(getState()));
            await dispatch(openAlreadyRegisteredDialog({}));
            return;
          } else if (getRegisterErrors.isInviteeAlreadyWaitlistedForClosedEvent(waitlistError)) {
            changePage(getDefaultWebsitePageId(getState()));
            await dispatch(openEventWaitlistDialog());
            return;
          } else if (getRegisterErrors.isPrivateEvent(waitlistError)
            || getRegisterErrors.isAttendeeNotAllowedByCustomLogic(waitlistError)) {
            await dispatch(openPrivateEventErrorDialog());
            changePage(getDefaultWebsitePageId(getState()));
            return;
          } else if (getRegisterErrors.isEventTemporaryClosed(waitlistError)) {
            if (getRegisterErrors.isEventClosed(error)) {
              dispatch(openEventStatusDialog(eventStatus.CLOSED, getState().text.translate));
              return;
            }
            await dispatch(openEventTemporaryClosedErrorDialog());
            return;
          }
          throw waitlistError;
        }
        await dispatch(loadRegistrationContent(WAITLIST, getRegistrationPathIdOrNull(getState())));
        changePage(WAITLIST.forCurrentRegistrant().startPageId(getState()));
        return;
      }
      if (getRegisterErrors.isRegistrationClosed(error) || getRegisterErrors.isEventClosed(error)) {
        changePage(getDefaultWebsitePageId(getState()));
        dispatch(openEventStatusDialog(eventStatus.CLOSED, getState().text.translate));
        return;
      }
      if (getCheckoutErrors.admissionItemMissing(error)) {
        dispatch(openNoAdmissionItemAvailableForRegistrationTypeDialog(logoutPlannerAndRedirectToHome));
        return;
      }
      throw error;
    }
    const regTypeId = getRegistrationTypeId(getState());
    await dispatch(loadRegistrationContent(REGISTRATION, getRegistrationPathIdOrNull(getState())));
    await dispatch(loadGuestRegistrationContent(getRegistrationPathIdOrNull(getState())));
    if (regTypeId !== registrationTypeUtils.defaultRegistrationTypeId && hasRegTypeCapacityWarning(getState())) {
      const capacityFull = await regTypeCapacityFull(getState(),
        getAssociatedRegistrationPathId(getState(), regTypeId));
      const regTypeWidgetDoesNotExist = !getPageWithRegistrationType(getState());
      if (capacityFull || regTypeWidgetDoesNotExist) {
        changePage(getDefaultWebsitePageId(getState()));
        dispatch(openEventStatusDialog(eventStatus.CLOSED, getState().text.translate));
        return;
      }
    }
    changePage(REGISTRATION.forCurrentRegistrant().startPageId(getState()));
  };
}

/**
 *
 * In this method we pass the admin contact Id and create the reg cart with admin details populated
 *
 */
export function startAdminRegistration(changePageOverride = null) {
  return async (dispatch, getState) => {
    // Use history.replace or normal route change depending on scenario.
    const changePage = changePageOverride || (pageId => dispatch(routeToPage(pageId)));

    const initialState = getState();
    const { eventId, defaultRegTypeId, authenticatedContact } = initialState.userSession;
    try {
      await dispatch(startRegistration(eventId, null, null, defaultRegTypeId, authenticatedContact));
      const admin = getAdminPersonalInformation(getState());
      await dispatch(updateAdmin({
        ...admin,
        selectedValue: true }));
    } catch (error) {
      if (getRegisterErrors.hasExternalAuthenticationEnabled(error)) {
        redirectToExternalAuth(getState().event, getState().account);
        return;
      }
      if (getRegisterErrors.isEventTemporaryClosed(error)) {
        await dispatch(openEventTemporaryClosedErrorDialog());
        return;
      }
      if (getRegisterErrors.isRegistrationClosed(error) || getRegisterErrors.isEventClosed(error)) {
        changePage(getDefaultWebsitePageId(getState()));
        dispatch(openEventStatusDialog(eventStatus.CLOSED, getState().text.translate));
        return;
      }
      throw error;
    }
    const regTypeId = getRegistrationTypeId(getState());
    await dispatch(loadRegistrationContent(REGISTRATION, getRegistrationPathIdOrNull(getState())));
    await dispatch(loadGuestRegistrationContent(getRegistrationPathIdOrNull(getState())));
    if (regTypeId !== registrationTypeUtils.defaultRegistrationTypeId && hasRegTypeCapacityWarning(getState())) {
      const capacityFull = await regTypeCapacityFull(getState(),
        getAssociatedRegistrationPathId(getState(), regTypeId));
      const regTypeWidgetDoesNotExist = !getPageWithRegistrationType(getState());
      if (capacityFull || regTypeWidgetDoesNotExist) {
        changePage(getDefaultWebsitePageId(getState()));
        dispatch(openEventStatusDialog(eventStatus.CLOSED, getState().text.translate));
        return;
      }
    }
    changePage(REGISTRATION.forCurrentRegistrant().startPageId(getState()));
  };
}

function logoutPlannerAndRedirectToHome() {
  return (dispatch, getState) => {
    const { userSession: { isPlanner }, plannerRegSettings: { exitUrl } } = getState();
    if (isPlanner) {
      dispatch(logoutPlanner(exitUrl));
    }
    dispatch(redirectToPage(getDefaultWebsitePageId(getState())));
  };
}
